/**
 * State for the forgot-password flow, which spans three screens:
 *
 *   /forget-password  -> /otp-verify -> /create-new-password -> /login
 *   (step 1: request)     (step 2: verify)  (step 3: reset)
 *
 * Router state carries this between screens, but a refresh wipes router state, so it is
 * mirrored into sessionStorage. Every screen reads router state first and falls back to
 * here, which means a refresh mid-flow loses nothing - including the OTP expiry, so the
 * countdown resumes where it actually is rather than restarting at the full duration.
 *
 * The OTP itself is held because step 3 has to re-send it alongside the new password.
 * sessionStorage (not localStorage) so it dies with the tab, and the flow clears it on
 * completion, on failure, and whenever step 1 is opened afresh.
 *
 * Shape: { email, otpRequestId, otpExpiresAt, otp, otpVerified }
 */

const STORAGE_KEY = "passwordResetFlow";

// The OTP validity window the countdown on the verify screen ticks down.
export const OTP_TTL_SECONDS = 120;

export function getPasswordResetSession() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
}

/** Merges `values` into the stored session rather than replacing it. */
export function savePasswordResetSession(values) {
  const next = { ...getPasswordResetSession(), ...values };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return next;
}

export function clearPasswordResetSession() {
  sessionStorage.removeItem(STORAGE_KEY);
}

/** Absolute expiry (epoch ms) for an OTP issued now. */
export function otpExpiryFromNow() {
  return Date.now() + OTP_TTL_SECONDS * 1000;
}

/**
 * Seconds left on an OTP, floored at 0. Derived from the absolute expiry on every tick
 * rather than decrementing a counter, so it stays correct across refreshes and while
 * the tab is backgrounded.
 *
 * This is a display hint only. The service decides whether a code is still good - see
 * the note on the verify screen about codes that arrive after the countdown ends.
 */
export function otpSecondsRemaining(otpExpiresAt) {
  if (!otpExpiresAt) return 0;
  return Math.max(0, Math.ceil((otpExpiresAt - Date.now()) / 1000));
}

/**
 * Reads the flow state a screen needs, preferring router state (normal navigation) and
 * falling back to the store (refresh). One helper so all three screens agree.
 */
export function readPasswordResetFlow(locationState) {
  const stored = getPasswordResetSession();
  const fromRoute = locationState || {};
  return {
    email: fromRoute.email || stored.email || "",
    otpRequestId: fromRoute.otpRequestId || stored.otpRequestId || "",
    otpExpiresAt: fromRoute.otpExpiresAt || stored.otpExpiresAt || 0,
    otp: fromRoute.otp || stored.otp || "",
    otpVerified: Boolean(fromRoute.otpVerified || stored.otpVerified),
  };
}

/** Step 2 is reachable only once step 1 has produced a request id. */
export const canVerifyOtp = (flow) => Boolean(flow.otpRequestId) && !flow.otpVerified;

/** Step 3 is reachable only with a verified code still in hand. */
export const canResetPassword = (flow) =>
  Boolean(flow.otpVerified && flow.otpRequestId && flow.otp && flow.email);
